import React, {useEffect, useMemo, useState} from 'react';
import {useRoles} from '@/contexts/RolesContext';
import {ApiError, getApiService} from '@/services/api';
import {
  validateEmail,
  validatePasswords,
  validateRequired,
  validateRequiredArray
} from '@/utils/validation';
import {getUserFriendlyErrorMessage} from "@/utils/errorHandling.ts";
import {
  FormItem,
  InputType,
  ModalPopup, ModalSizeV1,
  SelectOption,
  useToast
} from "@fs/uikit-react";

interface User {
  id: string;
  username: string;
  email?: string;
  roles: {id: string, name: string}[];
}

interface UserFormModalProps {
  onClose: () => void;
  onUserSaved?: () => void;
  user?: User;
}

const UserFormModal: React.FC<UserFormModalProps> = ({
  onClose,
  onUserSaved,
  user
}) => {
  const isEditMode = !!user;
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState<string[]>([]);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [formValid, setFormValid] = useState(false);
  const [emailValid, setEmailValid] = useState(true);
  const [roleOptions, setRoleOptions] = useState<SelectOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { roles, error: rolesError } = useRoles();

  const apiService = useMemo(() => getApiService(), []);

  const toast = useToast();

  // Transform roles into SelectOption format for the SELECT input
  useEffect(() => {
    if (rolesError) {
      console.error('Error fetching roles:', rolesError);
    }

    // Transform roles into SelectOption format for the SELECT input
    if (roles && roles.length > 0) {
      const options: SelectOption[] = roles.map(role => ({
        value: role.id,
        label: role.name
      }));
      setRoleOptions(options);
    }
  }, [roles, rolesError]);

  // Reset or pre-fill form fields when opening modal
  useEffect(() => {
    if (isEditMode && user) {
      // Pre-fill form fields with user data when editing
      setUsername(user.username || '');
      setEmail(user.email || '');
      // Password fields are empty when editing - user will only set new password if they want to change it
      setPassword('');
      setConfirmPassword('');
      // Pre-fill roles
      const userRoleIds = user.roles.map(role => role.id);
      setRole(userRoleIds);
    } else {
      // Reset form fields when creating a new user
      setUsername('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setRole([]);
    }

    // Reset validation states
    setPasswordsMatch(true);
    setEmailValid(true);
    setFormValid(false);
  }, [isEditMode, user]);

  // Function to check if passwords match and update state
  const checkPasswordsMatch = (pass: string, confirm: string) => {
    const match = validatePasswords(pass, confirm);
    setPasswordsMatch(match);
    return match;
  };

  // Function to validate the entire form
  const validateForm = () => {
    const isEmailValid = validateEmail(email);
    setEmailValid(isEmailValid);

    let isFormValid;

    if (isEditMode) {
      // In edit mode, passwords are only required if one of them is filled
      const passwordsProvided = !!password || !!confirmPassword;
      const passwordsValid = !passwordsProvided || (validateRequired(password) && validateRequired(confirmPassword) && passwordsMatch);

      isFormValid = 
        validateRequired(username) && 
        validateRequiredArray(role) && 
        passwordsValid && 
        isEmailValid;
    } else {
      // In create mode, all fields are required
      isFormValid = 
        validateRequired(username) && 
        validateRequired(password) && 
        validateRequired(confirmPassword) && 
        validateRequiredArray(role) && 
        passwordsMatch && 
        isEmailValid;
    }

    setFormValid(isFormValid);
    return isFormValid;
  };

  // Validate form whenever any field changes
  useEffect(() => {
    validateForm();
  }, [username, email, password, confirmPassword, role, passwordsMatch, emailValid]);
  return (
    <ModalPopup
      onClose={onClose}
      title={isEditMode ? "Редагувати користувача" : "Створити користувача"}
      size={ModalSizeV1.SMALL}
      primaryButtonText="Зберегти"
      primaryButtonDisabled={!formValid || isSubmitting}
      onPrimaryButtonClick={async () => {
        // Final validation check before saving
        if (!validateForm()) {
          return;
        }

        // Map selected role IDs to role names
        const selectedRoleNames = role.map(roleId => {
          const selectedRole = roleOptions.find(option => option.value === roleId);
          return selectedRole ? selectedRole.label : '';
        }).filter(Boolean);

        // Prepare user data
        const userData: any = { 
          username, 
          email, 
          roles: selectedRoleNames
        };

        // Only include a password if it's provided (required for creation, optional for edit)
        if (password) {
          userData.password = password;
        }

        try {
          setIsSubmitting(true);

          let response;
          if (isEditMode && user) {
            // Make API request to update user
            response = await apiService.client.put(`/users/${user.id}`, userData);

            // Show success toast
            toast.showSuccessToast(
              'Користувача оновлено', 
              `Користувача ${username} успішно оновлено`
            );
          } else {
            // Make API request to create user
            response = await apiService.client.post('/users', userData);

            // Show success toast
            toast.showSuccessToast(
              'Користувача створено', 
              `Користувача ${username} успішно створено`
            );
          }

          // Call onUserSaved callback if provided
          if (onUserSaved) {
            onUserSaved();
          }

          // Close modal after a successful save
          onClose();
        } catch (error) {
          // Show error toast
          const action = isEditMode ? 'оновлення' : 'створення';
          toast.showErrorToast(
            `Помилка ${action} користувача`,
            getUserFriendlyErrorMessage(error as ApiError)
          );
          console.error(`Error ${isEditMode ? 'updating' : 'creating'} user:`, error);
        } finally {
          setIsSubmitting(false);
        }
      }}
      secondaryButtonText="Скасувати"
      onSecondaryButtonClick={onClose}
    >
      <div className="create-user-form">
        <FormItem
          label="Ім'я користувача"
          id="username"
          name="username"
          type={InputType.TEXT}
          placeholder="Введіть ім'я користувача"
          required={true}
          value={username}
          onChange={(e) => { setUsername(e.target.value); }}
        />
        <FormItem
          label="Email"
          id="email"
          name="email"
          type={InputType.EMAIL}
          placeholder="Введіть email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); }}
          error={!emailValid}
          errorMessage={!emailValid ? "Невірний формат email" : ""}
        />
        <FormItem
          label="Пароль"
          id="password"
          name="password"
          type={InputType.PASSWORD}
          placeholder="Введіть пароль"
          required={!isEditMode}
          value={password}
          onChange={(e) => {
            const newPassword = e.target.value;
            setPassword(newPassword);
            checkPasswordsMatch(newPassword, confirmPassword);
          }}
          typeSettings={{ showGeneratePasswordIcon: true }}
          onGeneratePassword={(newPassword) => {
            // Update both password fields
            setPassword(newPassword);
            setConfirmPassword(newPassword);
            // Validate passwords
            checkPasswordsMatch(newPassword, newPassword);
            // Show toast message
            toast.showInfoToast(
              'Пароль згенеровано',
              'Новий пароль було згенеровано та скопійовано в буфер обміну.'
            );
          }}
        />
        <FormItem
          label="Підтвердження пароля"
          id="confirmPassword"
          name="confirmPassword"
          type={InputType.PASSWORD}
          placeholder="Підтвердіть пароль"
          required={!isEditMode}
          value={confirmPassword}
          onChange={(e) => {
            const newConfirmPassword = e.target.value;
            setConfirmPassword(newConfirmPassword);
            checkPasswordsMatch(password, newConfirmPassword);
          }}
          error={!passwordsMatch}
          errorMessage={!passwordsMatch ? "Паролі не співпадають" : ""}
        />
        <FormItem
          label="Роль"
          id="role"
          name="role"
          type={InputType.MULTISELECT}
          placeholder="Виберіть роль"
          required={true}
          value={role}
          onChange={(e) => { setRole(e.target.value as string[]); }}
          options={roleOptions}
        />
      </div>
    </ModalPopup>
  );
};

export default UserFormModal;
